import {
  BufferGeometry,
  Float32BufferAttribute,
  Group,
  Mesh,
  type Material,
} from "three";

import { CROSSING_DETAIL_RENDER_CONFIG as CONFIG } from
  "../../config/crossingDetails.ts";
import type {
  RuntimeLinearFeature,
  RuntimeRoadControl,
  RuntimeWorldTilePayloadV2,
} from "../../world/schema/runtimeTile.ts";
import type { TerrainTileData } from "../../world/schema/tile.ts";
import {
  streetFeatureRoadEdgeWidthM,
  streetFeatureSurfaceElevationM,
} from "../../world/streets.ts";
import type { CityMaterialLibrary } from "../materials/cityMaterials.ts";

export interface CrossingDetailBatch {
  readonly object: Group;
  readonly tactileCrossingCount: number;
  readonly loweredKerbCrossingCount: number;
  readonly refugeIslandCount: number;
  readonly raisedTableCount: number;
  readonly cycleCrossingCount: number;
}

interface MeshData {
  readonly positions: number[];
  readonly indices: number[];
}

interface DetailLayer {
  readonly name: string;
  readonly material: Material;
  readonly mesh: MeshData;
}

export function createCrossingDetailBatch(
  tile: Readonly<RuntimeWorldTilePayloadV2>,
  terrain: Readonly<TerrainTileData>,
  materials: CityMaterialLibrary,
): CrossingDetailBatch | null {
  const controls = (tile.roadControls ?? []).filter((control) =>
    control.kind === "crossing" && (
      control.tactilePaving === true ||
      control.kerb === "lowered" ||
      control.kerb === "flush" ||
      control.refugeIsland === true ||
      control.raisedTable === true ||
      control.cycleCrossing === true
    )
  );
  if (controls.length === 0) return null;
  const tactileLayer = layer("tactile-paving", materials.busStopTactile);
  const kerbLayer = layer(
    "dropped-kerb-ramps",
    materials.streetSurfaces.general.sidewalk,
  );
  const islandLayer = layer(
    "refuge-islands",
    materials.streetSurfaces.general.curb,
  );
  const tableLayer = layer(
    "raised-crossing-tables",
    materials.streetSurfaces.general.carriageway,
  );
  const cycleLayer = layer("cycle-crossing-boundaries", materials.laneMarking);
  const layers = [
    tactileLayer,
    kerbLayer,
    islandLayer,
    tableLayer,
    cycleLayer,
  ];
  const roads = tile.transportLines.filter((feature) => feature.kind === "road");
  let tactileCrossingCount = 0;
  let loweredKerbCrossingCount = 0;
  let refugeIslandCount = 0;
  let raisedTableCount = 0;
  let cycleCrossingCount = 0;
  for (const control of controls) {
    const road = roads.find((candidate) =>
      sourceFeatureId(candidate) === control.roadFeatureId
    );
    if (road === undefined) continue;
    const tangent = control.tangent;
    const normal = { x: -tangent.z, z: tangent.x };
    const halfRoadWidthM = streetFeatureRoadEdgeWidthM(road) / 2;
    if (control.raisedTable === true) {
      appendRoadRectangle(
        tableLayer.mesh,
        control,
        road,
        terrain,
        tile,
        0,
        0,
        CONFIG.raisedTableLengthM,
        halfRoadWidthM * 2,
        CONFIG.raisedTableLiftM,
      );
      raisedTableCount += 1;
    }
    if (control.tactilePaving === true) {
      for (const side of [-1, 1] as const) {
        appendRoadRectangle(
          tactileLayer.mesh,
          control,
          road,
          terrain,
          tile,
          0,
          0,
          CONFIG.tactileDepthM,
          Math.min(CONFIG.tactileWidthM, halfRoadWidthM * 0.8),
          CONFIG.surfaceLiftM,
          normal,
          side * (halfRoadWidthM + CONFIG.tactileDepthM / 2),
        );
      }
      tactileCrossingCount += 1;
    }
    if (control.kerb === "lowered" || control.kerb === "flush") {
      for (const side of [-1, 1] as const) {
        appendRoadRectangle(
          kerbLayer.mesh,
          control,
          road,
          terrain,
          tile,
          0,
          0,
          CONFIG.kerbRampDepthM,
          Math.min(CONFIG.tactileWidthM, halfRoadWidthM * 0.8),
          CONFIG.surfaceLiftM * 0.5,
          normal,
          side * (halfRoadWidthM + CONFIG.kerbRampDepthM / 2),
        );
      }
      loweredKerbCrossingCount += 1;
    }
    if (control.refugeIsland === true) {
      appendRoadRectangle(
        islandLayer.mesh,
        control,
        road,
        terrain,
        tile,
        0,
        0,
        CONFIG.refugeIslandLengthM,
        CONFIG.refugeIslandWidthM,
        0.11,
      );
      refugeIslandCount += 1;
    }
    if (control.cycleCrossing === true) {
      appendCycleBoundary(
        cycleLayer.mesh,
        control,
        road,
        terrain,
        tile,
        CONFIG.cycleBoundaryOffsetM,
      );
      appendCycleBoundary(
        cycleLayer.mesh,
        control,
        road,
        terrain,
        tile,
        -CONFIG.cycleBoundaryOffsetM,
      );
      cycleCrossingCount += 1;
    }
  }
  const object = new Group();
  object.name = "runtime-crossing-details:" + tile.id;
  object.userData = {
    tileId: tile.id,
    sourceLayer: "openstreetmap-crossing-tags-offline-matched-to-grpk",
    sourceLicense: "ODbL-1.0",
    inferredDetails: false,
  };
  for (const item of layers) {
    if (item.mesh.indices.length === 0) continue;
    const geometry = new BufferGeometry();
    geometry.setAttribute(
      "position",
      new Float32BufferAttribute(item.mesh.positions, 3),
    );
    geometry.setIndex(item.mesh.indices);
    geometry.computeVertexNormals();
    geometry.computeBoundingBox();
    geometry.computeBoundingSphere();
    const mesh = new Mesh(geometry, item.material);
    mesh.name = object.name + ":" + item.name;
    mesh.castShadow = false;
    mesh.receiveShadow = true;
    mesh.renderOrder = 4;
    object.add(mesh);
  }
  return object.children.length === 0
    ? null
    : {
        object,
        tactileCrossingCount,
        loweredKerbCrossingCount,
        refugeIslandCount,
        raisedTableCount,
        cycleCrossingCount,
      };
}

function appendCycleBoundary(
  mesh: MeshData,
  control: Readonly<RuntimeRoadControl>,
  road: Readonly<RuntimeLinearFeature>,
  terrain: Readonly<TerrainTileData>,
  tile: Readonly<RuntimeWorldTilePayloadV2>,
  normalOffsetM: number,
): void {
  const pitchM = CONFIG.cycleDashLengthM + CONFIG.cycleDashGapM;
  for (
    let alongM = -CONFIG.crossingHalfLengthM;
    alongM < CONFIG.crossingHalfLengthM;
    alongM += pitchM
  ) {
    appendRoadRectangle(
      mesh,
      control,
      road,
      terrain,
      tile,
      0,
      0,
      Math.min(
        CONFIG.cycleDashLengthM,
        CONFIG.crossingHalfLengthM - alongM,
      ),
      CONFIG.cycleLineWidthM,
      CONFIG.surfaceLiftM,
      control.tangent,
      alongM + CONFIG.cycleDashLengthM / 2,
      normalOffsetM,
    );
  }
}

function appendRoadRectangle(
  mesh: MeshData,
  control: Readonly<RuntimeRoadControl>,
  road: Readonly<RuntimeLinearFeature>,
  terrain: Readonly<TerrainTileData>,
  tile: Readonly<RuntimeWorldTilePayloadV2>,
  localOffsetX: number,
  localOffsetZ: number,
  alongLengthM: number,
  acrossWidthM: number,
  liftM: number,
  offsetAxis: Readonly<{ x: number; z: number }> = control.tangent,
  offsetM = 0,
  secondaryOffsetM = 0,
): void {
  const tangent = control.tangent;
  const normal = { x: -tangent.z, z: tangent.x };
  const centre = {
    x: control.roadPosition.x + localOffsetX + offsetAxis.x * offsetM +
      normal.x * secondaryOffsetM,
    z: control.roadPosition.z + localOffsetZ + offsetAxis.z * offsetM +
      normal.z * secondaryOffsetM,
  };
  const halfAlong = alongLengthM / 2;
  const halfAcross = acrossWidthM / 2;
  const corners = [
    {
      x: centre.x - tangent.x * halfAlong - normal.x * halfAcross,
      z: centre.z - tangent.z * halfAlong - normal.z * halfAcross,
    },
    {
      x: centre.x + tangent.x * halfAlong - normal.x * halfAcross,
      z: centre.z + tangent.z * halfAlong - normal.z * halfAcross,
    },
    {
      x: centre.x + tangent.x * halfAlong + normal.x * halfAcross,
      z: centre.z + tangent.z * halfAlong + normal.z * halfAcross,
    },
    {
      x: centre.x - tangent.x * halfAlong + normal.x * halfAcross,
      z: centre.z - tangent.z * halfAlong + normal.z * halfAcross,
    },
  ];
  const heights = corners.map((corner) =>
    streetFeatureSurfaceElevationM(road, terrain, tile.origin, corner)
  );
  if (heights.some((height) => height === null)) return;
  const base = mesh.positions.length / 3;
  for (const [index, corner] of corners.entries()) {
    mesh.positions.push(corner.x, heights[index]! + liftM, corner.z);
  }
  mesh.indices.push(base, base + 1, base + 2, base, base + 2, base + 3);
}

function layer(name: string, material: Material): DetailLayer {
  return {
    name,
    material,
    mesh: { positions: [], indices: [] },
  };
}

function sourceFeatureId(feature: Readonly<RuntimeLinearFeature>): string {
  return feature.id.split("#", 1)[0] ?? feature.id;
}
